<template>
  <div class="flex items-center gap-4 mb-4">
    <div class="grid grid-cols-1 w-full">
      <label
        v-if="!isSubmitting"
        for="dropzone-file-supabase"
        class="w-full p-5 text-center border-2 border-dashed rounded-lg cursor-pointer border-gray-80"
        :class="{ 'bg-blue-50': isDragging }"
        @dragover.prevent="onDragOver"
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div class="flex items-center justify-center ">
          <div class="grid grid-cols-1">
            <div>
              <Icon name="solar:upload-minimalistic-bold-duotone" />
            </div>
            <div class="mb-2 text-sm">
              <span class="font-semibold">
                Drag and drop your files here or browse!
              </span>
            </div>
          </div>
        </div>
        <input
          id="dropzone-file-supabase"
          type="file"
          class="hidden"
          :accept="accept"
          :multiple="multipleFilesUpload"
          @change="handleFileChange"
        >
      </label>
      <div
        v-else
        class="w-full p-5 text-center border-2 border-dashed rounded-lg cursor-pointer border-gray-80"
      >
        <div class="flex items-center justify-center ">
          <div class="grid grid-cols-1">
            <div class="text-sm">
              <span class="font-semibold">
                Submitting...
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="selectedFiles && selectedFiles.length > 0"
        class="grid grid-cols-1 gap-y-1 divide-y border rounded mt-2"
      >
        <div
          v-for="(file, index) in selectedFiles"
          :key="index"
          class="flex items-center gap-2 p-2"
        >
          <div class="w-full relative">
            <div class="pr-12">
              {{ file.name }}
            </div>
            <span
              v-if="isSubmitting"
              class="absolute right-6 top-1/2 -translate-y-1/2 flex items-center"
            >
              <Icon
                v-if="uploadStatus[index]"
                name="lucide:circle-check"
                class="text-green-500 w-4 h-4"
              />
              <Icon
                v-else
                name="lucide:loader-2"
                class="text-gray-400 w-4 h-4 animate-spin"
              />
            </span>
            <span
              class="absolute right-0 top-1/2 -translate-y-1/2 flex items-center"
              :class="isSubmitting ? 'pointer-events-none opacity-40' : 'cursor-pointer'"
              @click="!isSubmitting && selectedFiles.splice(index, 1)"
            >
              <Icon
                name="lucide:trash-2"
                class="text-red-500 w-4 h-4"
              />
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const { getSupabaseSignedUploadUrl, registerSupabaseFile, deleteSupabaseObject } = useStorage()

const toast = useToast()
const isDragging = ref(false)
const selectedFiles = ref<File[]>([])
const uploadStatus = ref<Record<number, boolean>>({})

const props = defineProps<{
  uploadedBy?: string
  accept?: string
}>()

const isSubmitting = defineModel<boolean>('isSubmitting', {
  type: Boolean,
  required: false,
  default: false,
})

const multipleFilesUpload = defineModel<boolean>('multipleFilesUpload', {
  type: Boolean,
  required: false,
  default: true,
})

const folderId = defineModel<string>('folderId', {
  type: String,
  required: true,
})

const uploadedFiles = defineModel<Array<object>>('uploadedFiles', {
  type: Array,
  required: false,
  default: [],
})

const onDragOver = () => {
  isDragging.value = true
}

const onDragLeave = () => {
  isDragging.value = false
}

const onDrop = (event: DragEvent) => {
  isDragging.value = false
  handleFileChangeFromDnD(event.dataTransfer?.files || null)
}

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

const addFiles = (files: FileList) => {
  const incoming = Array.from(files)
  const oversized = incoming.filter(file => file.size >= MAX_FILE_SIZE)
  const allowed = incoming.filter(file => file.size < MAX_FILE_SIZE)

  if (oversized.length > 0) {
    toast.add({
      severity: 'warn',
      summary: 'File too large',
      detail: `Max file size is 50MB. Skipped: ${oversized.map(file => file.name).join(', ')}`,
      life: 4000,
    })
  }

  if (allowed.length > 0) {
    selectedFiles.value = selectedFiles.value.concat(allowed)
  }
}

const handleFileChangeFromDnD = (files: FileList | null) => {
  if (multipleFilesUpload.value === false && selectedFiles.value.length >= 1) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'You can only upload 1 file', life: 3000 })
    return
  }

  if (files) {
    addFiles(files)
  }
}

const handleFileChange = (event: Event) => {
  if (multipleFilesUpload.value === false && selectedFiles.value.length >= 1) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'You can only upload 1 file', life: 3000 })
    return
  }

  const input = event.target as HTMLInputElement
  if (input.files) {
    addFiles(input.files)
  }
}

const upload = async () => {
  if (!folderId.value) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Folder is required', life: 3000 })
    return
  }

  isSubmitting.value = true
  uploadStatus.value = {}

  // Tracks an object that was uploaded (PUT) but not yet registered, so it can
  // be cleaned up if registration fails (prevents orphaned storage objects).
  let orphanStoragePath: string | null = null

  try {
    const results: object[] = []

    for (let i = 0; i < selectedFiles.value.length; i++) {
      const selectedFile = selectedFiles.value[i]
      orphanStoragePath = null
      const signedUpload = await getSupabaseSignedUploadUrl({
        folderId: folderId.value,
        fileName: selectedFile.name,
      })

      const uploadResponse = await fetch(signedUpload.data.signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': selectedFile.type || 'application/octet-stream',
        },
        body: selectedFile,
      })

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload ${selectedFile.name}`)
      }

      // PUT succeeded; the object now exists but has no DB row yet.
      orphanStoragePath = signedUpload.data.storagePath

      const registerResponse = await registerSupabaseFile({
        folderId: folderId.value,
        name: selectedFile.name,
        storagePath: signedUpload.data.storagePath,
        uploadedBy: props.uploadedBy,
        dropboxFileType: selectedFile.type,
        dropboxFileSize: selectedFile.size,
      })

      orphanStoragePath = null
      uploadStatus.value[i] = true
      results.push(registerResponse?.data)
    }

    uploadedFiles.value = results
    selectedFiles.value = []
    return results
  }
  catch (error: any) {
    // Compensate an orphaned object (PUT succeeded, register failed) — best effort.
    if (orphanStoragePath) {
      await deleteSupabaseObject(orphanStoragePath).catch(() => {})
    }
    toast.add({
      severity: 'error',
      summary: 'Upload failed',
      detail: error?.data?.statusMessage || error?.message || 'Something went wrong',
      life: 4000,
    })
    throw error
  }
  finally {
    isSubmitting.value = false
  }
}

defineExpose({
  upload,
})
</script>
